.panel {
  background: var(--surface);
  border-radius: var(--radius);
  padding: 24px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
}

.label {
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
}

.total {
  font-size: 0.88rem;
  font-variant-numeric: tabular-nums;
  color: var(--muted);
}

/* Names the dashed reference line in the rows, drawn the same way. */
.legendDash {
  border-bottom: 1px dashed var(--soft);
  padding-bottom: 1px;
}

/* The radar reads as shape, the body map as anatomy — side by side, the way
   they were before they shared a heading. */
.views {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.shape {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.row {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr) 74px 128px 56px;
  align-items: center;
  gap: 12px;
}

/* Without a week series there is no sparkline column to lay out. */
.rowsNoSpark .row {
  grid-template-columns: 96px 128px 56px;
}

.name {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--soft);
  white-space: nowrap;
}

.sparkCell {
  min-width: 0;
}

.spark {
  display: block;
  width: 100%;
  height: 46px;
}

.line {
  fill: none;
  stroke: var(--amber);
  stroke-width: 1.5;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.lineZero {
  fill: none;
  stroke: var(--border-md);
  stroke-width: 1.5;
}

.dot {
  fill: var(--amber);
}

/* A week with nothing logged for this muscle: the line still drops to the
   baseline, and the dot marks it as trained-nothing rather than missing. */
.dotZero {
  fill: var(--border-md);
}

.hit {
  fill: transparent;
  cursor: crosshair;
}

.average {
  stroke: var(--soft);
  stroke-width: 1;
  stroke-dasharray: 3 3;
  opacity: 0.5;
}

.guide {
  stroke: var(--border-md);
  stroke-width: 1;
}

.tooltip {
  position: fixed;
  z-index: 9999;
  background: var(--canvas);
  border: 1px solid var(--border-md);
  border-radius: 8px;
  padding: 8px 12px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
  box-shadow: 0 6px 20px rgba(38, 33, 25, 0.18);
}

.tooltipLabel {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--amber-strong);
}

.tooltipValue {
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
  color: var(--soft);
  white-space: nowrap;
}

.tooltipAvg {
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  color: var(--muted);
  white-space: nowrap;
  padding-top: 3px;
  border-top: 1px solid var(--border);
  margin-top: 2px;
}

.rate {
  font-size: 0.85rem;
  color: var(--soft);
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.rate em {
  font-style: normal;
  color: var(--muted);
}

.stat {
  font-size: 0.85rem;
  color: var(--muted);
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.delta {
  font-size: 0.85rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}

.up {
  color: var(--up);
}

.down {
  color: var(--down);
}

.flat {
  color: var(--flat);
}

@media (max-width: 900px) {
  .views {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }
}

@media (max-width: 620px) {
  /* A phone column is too narrow for a legible sparkline next to the numbers,
     so the row becomes two lines with the chart across the full width. */
  .row {
    grid-template-columns: minmax(0, 1fr) auto 54px;
    grid-template-areas:
      "name stat delta"
      "spark spark spark";
    row-gap: 2px;
  }

  .rowsNoSpark .row {
    grid-template-columns: minmax(0, 1fr) auto 54px;
    grid-template-areas: "name stat delta";
  }

  .name { grid-area: name; }
  .stat { grid-area: stat; }
  .delta { grid-area: delta; }
  .sparkCell { grid-area: spark; }

  .rows {
    gap: 12px;
  }

  /* The weekly rate is the first thing to go: the sparkline carries the shape
     and the total carries the size. */
  .rate {
    display: none;
  }

  .headingExtra {
    display: none;
  }
}
